import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type TASProgramTemplateModel from 'tio-common/models/tas-program-template';
import type TASProgramInstanceModel from 'tio-common/models/tas-program-instance';
import { filterEligibleTemplates } from 'tio-common/utils/tuition-assistance/filter-eligibile-templates';
import type EmployeeModel from 'tio-common/models/employee';
import type { CompanySettingTuitionAssistance } from 'tio-common/models/company-setting';

type Transition = ReturnType<RouterService['transitionTo']>;

export default class AuthenticatedTasDashboardRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  async model() {
    const currentEmployee = this.sessionContext.currentEmployee;

    const isDirectPayEnabled =
      (
        currentEmployee.company?.companySetting?.tuitionAssistance as
          | CompanySettingTuitionAssistance
          | undefined
      )?.isDirectPayEnabled || false;
    const states = isDirectPayEnabled
      ? ['TAS.ProgramTemplateState.LAUNCHED', 'TAS.ProgramTemplateState.SUSPENDED']
      : 'TAS.ProgramTemplateState.LAUNCHED';

    let templates = await this.store.query<TASProgramTemplateModel>('tas-program-template', {
      filter: {
        state: states,
        byEligibility: currentEmployee.id,
        company: currentEmployee.company.id,
      },
      include: 'company.company-setting',
      reload: true,
      page: {
        limit: 100,
      },
    });

    const promises = [
      // Load the TasParticipant fresh to ensure we alert any HELD participant state
      // @ts-expect-error type models
      this.store.findRecord<EmployeeModel>('employee', currentEmployee.id, {
        include: 'tas-participant,company',
        reload: true,
      }),
      this.tuitionAssistance.getApplicationLimitStatusByEmployeeId(currentEmployee),
      this.store.query<TASProgramInstanceModel>('tas-program-instance', {
        filter: { employee: currentEmployee.id, active: true },
        include: 'tas-program-template,tas-applications',
        page: {
          limit: 200,
        },
      }),
      this.store.query<TASProgramInstanceModel>('tas-program-instance', {
        filter: { employee: currentEmployee.id, past: true },
        include: 'tas-program-template,tas-applications',
        page: {
          limit: 100,
        },
      }),
    ];

    const currentUserCanViewTrialTemplates =
      this.sessionContext.user.isEmployeeAdmin || this.sessionContext.user.isAccountOwner;

    if (currentUserCanViewTrialTemplates) {
      promises.push(
        // @ts-expect-error who knows
        this.store.query<TASProgramTemplateModel>('tas-program-template', {
          filter: {
            state: 'TAS.ProgramTemplateState.LAUNCHED',
            isTrial: true,
            company: currentEmployee.company.id,
          },
          include: 'company.company-setting',
        })
      );
    }

    const [
      employeeWithParticipants,
      limitsStatus,
      activeInstances,
      pastInstances,
      trialTemplates = [],
    ] = await Promise.all(promises);
    // @ts-expect-error who knows
    const tasParticipantId = employeeWithParticipants.tasParticipant.id;

    const [dynamicEligibility, programEligibility] = await Promise.all([
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('tas-participant').dynamicEligibility(tasParticipantId),
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('tas-participant').periodOfEligibility(tasParticipantId),
    ]);

    if (currentUserCanViewTrialTemplates) {
      // @ts-expect-error not sure
      templates = [...templates, ...trialTemplates];
    }

    const templatesFilteredByEligibility = filterEligibleTemplates(
      templates,
      dynamicEligibility,
      programEligibility
    );

    const isEmployeeProgram = (program: TASProgramTemplateModel) => {
      return program.isDependentProgram === false;
    };

    const isEmployeeInstance = (instance: TASProgramInstanceModel) => {
      return instance.isDependentProgram === false;
    };

    const filteredTemplates = templatesFilteredByEligibility.filter(isEmployeeProgram);
    // @ts-expect-error not sure
    const filteredActiveInstances = activeInstances.filter(isEmployeeInstance);
    // @ts-expect-error not sure
    const filteredPastInstances = pastInstances.filter(isEmployeeInstance);

    return {
      templates: filteredTemplates,
      activeInstances: filteredActiveInstances,
      pastInstances: filteredPastInstances,
      // @ts-expect-error not sure
      isHeldParticipant: !!employeeWithParticipants.tasParticipant?.isHeld,
      limitsStatus,
    };
  }
}
